// types/leaderboard.ts
export enum Difficulty {
    EASY       = 'Easy',
    MEDIUM     = 'Medium',
    HARD       = 'Hard',
}

export enum Duration {
    S1M       = '1 Minute',
    S3M       = '3 Minutes',
    S5M       = '5 Minutes',
}

// We can't compute an enum such that Competition1minEasy= `${Duration.S1M} - ${Difficulty.EASY}`, so we use a const
export const ConstCategoryLabel = {
    GrandChallenge:         'Grand Challenge',
    Competition1minEasy:    `${Duration.S1M} - ${Difficulty.EASY}`,
    Competition1minMedium:  `${Duration.S1M} - ${Difficulty.MEDIUM}`,
    Competition1minHard:    `${Duration.S1M} - ${Difficulty.HARD}`,
    Competition3minEasy:    `${Duration.S3M} - ${Difficulty.EASY}`,
    Competition3minMedium:  `${Duration.S3M} - ${Difficulty.MEDIUM}`,
    Competition3minHard:    `${Duration.S3M} - ${Difficulty.HARD}`,
    Competition5minEasy:    `${Duration.S5M} - ${Difficulty.EASY}`,
    Competition5minMedium:  `${Duration.S5M} - ${Difficulty.MEDIUM}`,
    Competition5minHard:    `${Duration.S5M} - ${Difficulty.HARD}`,
    Questions100Easy:       `100 Questions - ${Difficulty.EASY}`,
    Questions100Medium:     `100 Questions - ${Difficulty.MEDIUM}`,
    Questions100Hard:       `100 Questions - ${Difficulty.HARD}`,
};

export type CategoryLabel = typeof ConstCategoryLabel[keyof typeof ConstCategoryLabel];

export enum ScoreCategory {
    // Grand Challenge
    GrandChallenge = 'grand_challenge',
  
    // Timed (Competition) - 1 minute
    Competition1minEasy     = 'timed_1_min_easy',
    Competition1minMedium   = 'timed_1_min_medium',
    Competition1minHard     = 'timed_1_min_hard',
  
    // Timed (Competition) - 3 minutes
    Competition3minEasy     = 'timed_3_min_easy',
    Competition3minMedium   = 'timed_3_min_medium',
    Competition3minHard     = 'timed_3_min_hard',
  
    // Timed (Competition) - 5 minutes
    Competition5minEasy     = 'timed_5_min_easy',
    Competition5minMedium   = 'timed_5_min_medium',
    Competition5minHard     = 'timed_5_min_hard',
  
    // 100 Questions
    Questions100Easy        = 'questions_100_easy',
    Questions100Medium      = 'questions_100_medium',
    Questions100Hard        = 'questions_100_hard',
}

export enum CompetitionType {
    Timed       = 'timed',
    Questions   = 'questions',
    Competition = 'competition',
}

export interface CategoryConfig {
    key:   ScoreCategory;
    label: CategoryLabel;
    description?: string;
    group: CompetitionType;
}
  
export const CATEGORIES: Record<ScoreCategory, CategoryConfig> = {
    grand_challenge: {
      key:   ScoreCategory.GrandChallenge,
      label: ConstCategoryLabel.GrandChallenge,
      group: CompetitionType.Competition,
    },
    timed_1_min_easy: {
      key:   ScoreCategory.Competition1minEasy,
      label: ConstCategoryLabel.Competition1minEasy,
      group: CompetitionType.Timed,
    },
    timed_1_min_medium: {
      key:   ScoreCategory.Competition1minMedium,
      label: ConstCategoryLabel.Competition1minMedium,
      group: CompetitionType.Timed,
    },
    timed_1_min_hard: {
      key:   ScoreCategory.Competition1minHard,
      label: ConstCategoryLabel.Competition1minHard,
      group: CompetitionType.Timed,
    },
    timed_3_min_easy: {
      key:   ScoreCategory.Competition3minEasy,
      label: ConstCategoryLabel.Competition3minEasy,
      group: CompetitionType.Timed,
    },
    timed_3_min_medium: {
      key:   ScoreCategory.Competition3minMedium,
      label: ConstCategoryLabel.Competition3minMedium,
      group: CompetitionType.Timed,
    },
    timed_3_min_hard: {
      key:   ScoreCategory.Competition3minHard,
      label: ConstCategoryLabel.Competition3minHard,
      group: CompetitionType.Timed,
    },
    timed_5_min_easy: {
      key:   ScoreCategory.Competition5minEasy,
      label: ConstCategoryLabel.Competition5minEasy,
      group: CompetitionType.Timed,
    },
    timed_5_min_medium: {
      key:   ScoreCategory.Competition5minMedium,
      label: ConstCategoryLabel.Competition5minMedium,
      group: CompetitionType.Timed,
    },
    timed_5_min_hard: {
      key:   ScoreCategory.Competition5minHard,
      label: ConstCategoryLabel.Competition5minHard,
      group: CompetitionType.Timed,
    },
    questions_100_easy: {
      key:   ScoreCategory.Questions100Easy,
      label: ConstCategoryLabel.Questions100Easy,
      group: CompetitionType.Questions,
    },
    questions_100_medium: {
      key:   ScoreCategory.Questions100Medium,
      label: ConstCategoryLabel.Questions100Medium,
      group: CompetitionType.Questions,
    },
    questions_100_hard: {
      key:   ScoreCategory.Questions100Hard,
      label: ConstCategoryLabel.Questions100Hard,
      group: CompetitionType.Questions,
    },
};
  
export interface LeaderboardScore {
    display_name: string;
    user_id: string;
    score: number;
    at: number; // unix seconds
}
  
  // Bulk weekly response for all categories
export interface WeeklyLeaderboardAllResponse {
    weekOf: string; // "YYYY-MM-DD"
    scoresByCategory: Record<ScoreCategory, LeaderboardScore[]>;
}
  
export interface AllTimeLeaderboardItem {
    display_name: string;
    user_id: string;
    score: number;
    at: number;
}
  
  // Bulk all-time response for all categories
export interface AllTimeLeaderboardAllResponse {
    scoresByCategory: Record<ScoreCategory, LeaderboardScore[]>;
}

// Shared score response from GET /score/:gameId
export interface SharedScoreResponse {
    id: string;
    score: number;
    playedAt: number; // unix timestamp (seconds)
    rank: number | null;
    player: {
        displayName: string;
        userId: string | null;
    };
    game: {
        type: string; // "timed", "questions", or "competition"
        difficulty: string | null; // "Easy", "Medium", "Hard", or null
        length: string | null; // "1 Minute", "3 Minutes", "5 Minutes", "100 Questions", or null
        category: ScoreCategory | null;
        categoryLabel: string;
    };
    platform: string | null; // "web", "ios", "android", or null
}
